import { Preferences } from '@capacitor/preferences';
import { Device } from '@capacitor/device';

class BiometricAuthService {
  constructor() {
    this.isAvailable = false;
    this.isEnabled = false;
    this.authMethod = null;
  }

  // Check if biometric authentication is available
  async checkBiometricAvailability() {
    try {
      const isMobile = await this.isMobileDevice();
      
      if (!isMobile) {
        // For web browsers, check WebAuthn support
        this.isAvailable = 'credentials' in navigator && 'create' in navigator.credentials;
        this.authMethod = 'webauthn';
        return this.isAvailable;
      }

      // For mobile devices, check device capabilities
      const info = await Device.getInfo();
      
      // Check if device has biometric sensors
      if (info.platform === 'android' || info.platform === 'ios') {
        this.isAvailable = true;
        this.authMethod = info.platform === 'ios' ? 'faceid' : 'fingerprint';
      } else {
        this.isAvailable = false;
        this.authMethod = null;
      }

      return this.isAvailable;
    } catch (error) {
      console.error('Error checking biometric availability:', error);
      this.isAvailable = false;
      return false;
    }
  }

  // Check if biometric is enabled for this app
  async isBiometricEnabled() {
    try {
      const { value } = await Preferences.get({ key: 'biometric_enabled' });
      this.isEnabled = value === 'true';
      return this.isEnabled;
    } catch (error) {
      console.error('Error checking biometric enabled status:', error);
      return false;
    }
  }

  // Enable biometric authentication
  async enableBiometric() {
    try {
      const isAvailable = await this.checkBiometricAvailability();
      
      if (!isAvailable) {
        throw new Error('Biometric authentication is not available on this device');
      }

      // Test biometric authentication
      const testResult = await this.authenticate('Test authentication');
      
      if (testResult.success) {
        await Preferences.set({ key: 'biometric_enabled', value: 'true' });
        this.isEnabled = true;
        return true;
      } else {
        throw new Error('Biometric authentication test failed');
      }
    } catch (error) {
      console.error('Error enabling biometric:', error);
      throw error;
    }
  }

  // Disable biometric authentication
  async disableBiometric() {
    try {
      await Preferences.set({ key: 'biometric_enabled', value: 'false' });
      this.isEnabled = false;
      return true;
    } catch (error) {
      console.error('Error disabling biometric:', error);
      throw error;
    }
  }

  // Authenticate using biometric
  async authenticate(reason = 'Authenticate to access ACCTOO') {
    try {
      const isEnabled = await this.isBiometricEnabled();
      
      if (!isEnabled) {
        return { success: false, error: 'Biometric authentication is not enabled' };
      }

      const isAvailable = await this.checkBiometricAvailability();
      
      if (!isAvailable) {
        return { success: false, error: 'Biometric authentication is not available' };
      }

      if (this.authMethod === 'webauthn') {
        return await this.authenticateWebAuthn(reason);
      } else {
        return await this.authenticateMobile(reason);
      }
    } catch (error) {
      console.error('Error during biometric authentication:', error);
      return { success: false, error: error.message };
    }
  }

  // WebAuthn authentication for web browsers
  async authenticateWebAuthn(reason) {
    try {
      if (!('credentials' in navigator) || !('get' in navigator.credentials)) {
        throw new Error('WebAuthn not supported');
      }

      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: new Uint8Array(32),
          allowCredentials: [],
          userVerification: 'required',
          timeout: 60000
        }
      });

      return { success: true, credential };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Mobile biometric authentication
  async authenticateMobile(reason) {
    try {
      // For mobile devices, we'll simulate biometric authentication
      // In a real implementation, you would use a biometric plugin
      
      // Simulate authentication delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // For demo purposes, always return success
      // In production, integrate with actual biometric plugin
      return { success: true, method: this.authMethod };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Register biometric credential
  async registerBiometric(userId, userInfo) {
    try {
      if (this.authMethod === 'webauthn') {
        return await this.registerWebAuthn(userId, userInfo);
      } else {
        return await this.registerMobile(userId, userInfo);
      }
    } catch (error) {
      console.error('Error registering biometric:', error);
      throw error;
    }
  }

  // Register WebAuthn credential
  async registerWebAuthn(userId, userInfo) {
    try {
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: new Uint8Array(32),
          rp: { name: 'ACCTOO' },
          user: {
            id: new TextEncoder().encode(userId),
            name: userInfo.email,
            displayName: userInfo.name
          },
          pubKeyCredParams: [{ type: 'public-key', alg: -7 }],
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            userVerification: 'required'
          },
          timeout: 60000
        }
      });

      // Store credential ID for future authentication
      await Preferences.set({ 
        key: 'biometric_credential_id', 
        value: JSON.stringify(credential) 
      });

      return { success: true, credential };
    } catch (error) {
      throw new Error(`WebAuthn registration failed: ${error.message}`);
    }
  }

  // Register mobile biometric
  async registerMobile(userId, userInfo) {
    try {
      // For mobile devices, simulate registration
      // In production, integrate with actual biometric plugin
      
      await Preferences.set({ 
        key: 'biometric_user_id', 
        value: userId 
      });
      
      await Preferences.set({ 
        key: 'biometric_user_info', 
        value: JSON.stringify(userInfo) 
      });

      return { success: true };
    } catch (error) {
      throw new Error(`Mobile biometric registration failed: ${error.message}`);
    }
  }

  // Check if device is mobile
  async isMobileDevice() {
    try {
      const info = await Device.getInfo();
      return info.platform === 'ios' || info.platform === 'android';
    } catch (error) {
      return false;
    }
  }

  // Get biometric status
  async getBiometricStatus() {
    try {
      const isAvailable = await this.checkBiometricAvailability();
      const isEnabled = await this.isBiometricEnabled();
      
      return {
        isAvailable,
        isEnabled,
        authMethod: this.authMethod,
        canUse: isAvailable && isEnabled
      };
    } catch (error) {
      console.error('Error getting biometric status:', error);
      return {
        isAvailable: false,
        isEnabled: false,
        authMethod: null,
        canUse: false
      };
    }
  }

  // Initialize biometric service
  async initialize() {
    try {
      await this.checkBiometricAvailability();
      await this.isBiometricEnabled();
      return this.getBiometricStatus();
    } catch (error) {
      console.error('Error initializing biometric service:', error);
      return this.getBiometricStatus();
    }
  }
}

export default new BiometricAuthService();